import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Staff } from '@wsphere/staff/domain';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class RoleBundlesService {
  constructor(private http: HttpClient) {}

  async list(dto: Staff.Security.ListRoleBundlesQueryRequest) {
    return await firstValueFrom(
      this.http.get<Staff.Security.ListRoleBundlesQueryResponse>(`/api/role-bundles`, {
        params: {
          ...(dto.page && { page: dto.page }),
          ...(dto.limit && { limit: dto.limit }),
          ...(dto.search && { search: dto.search }),
          ...(dto.isActive !== undefined && { isActive: dto.isActive }),
          ...(dto.sortBy && { sortBy: dto.sortBy }),
          ...(dto.sortOrder && { sortOrder: dto.sortOrder }),
        },
      })
    );
  }

  async retrieve(bundleId: string) {
    return await firstValueFrom(this.http.get<Staff.Security.RoleBundle>(`/api/role-bundles/${bundleId}`));
  }

  async create(dto: Staff.Security.CreateRoleBundleRequest) {
    return await firstValueFrom(this.http.post<Staff.Security.CreateRoleBundleResponse>(`/api/role-bundles`, dto));
  }

  async update(bundleId: string, dto: Staff.Security.UpdateRoleBundleRequest) {
    return await firstValueFrom(
      this.http.patch<Staff.Security.UpdateRoleBundleResponse>(`/api/role-bundles/${bundleId}`, dto)
    );
  }

  async duplicate(bundleId: string, dto: Staff.Security.DuplicateRoleBundleRequest = {}) {
    return await firstValueFrom(
      this.http.post<Staff.Security.DuplicateRoleBundleResponse>(`/api/role-bundles/${bundleId}/duplicate`, dto)
    );
  }

  async setActive(bundleId: string, isActive: boolean) {
    return await firstValueFrom(
      this.http.patch<Staff.Security.DeactivateRoleBundleResponse>(`/api/role-bundles/${bundleId}/deactivate`, {
        isActive,
      })
    );
  }

  /** Pushes the bundle's current roles onto everyone holding it; omit `staffIds` to apply to all of them. */
  async apply(bundleId: string, dto: Staff.Security.ApplyRoleBundleRequest = {}) {
    return await firstValueFrom(
      this.http.post<Staff.Security.ApplyRoleBundleResponse>(`/api/role-bundles/${bundleId}/apply`, dto)
    );
  }

  /**
   * The bundles a staff member currently holds, derived from the bundleId stamped on their role assignments.
   * Scope with permissionKey (omit for tenant-wide) to match how they were assigned.
   */
  async listStaffBundles(staffId: string, permissionKey?: string) {
    return await firstValueFrom(
      this.http.get<Staff.Security.RoleBundle[]>(`/api/staff/role-bundles/${staffId}`, {
        params: { ...(permissionKey && { permissionKey }) },
      })
    );
  }

  async assignToStaff(dto: Staff.Security.AssignBundleToStaffRequest) {
    return await firstValueFrom(
      this.http.post<Staff.Security.AssignBundleToStaffResponse>(`/api/staff/role-bundles`, dto)
    );
  }

  async removeFromStaff(dto: Staff.Security.RemoveBundleFromStaffRequest) {
    return await firstValueFrom(
      this.http.delete<Staff.Security.RemoveBundleFromStaffResponse>(`/api/staff/role-bundles`, { params: { ...dto } })
    );
  }
}
